import { FC, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import styles from './Faq.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import { UiAppearance } from '~/modules/UiVariant/appearance'

interface Props {
  appearance?: UiAppearance
  showTitle?: boolean
}

const howToMintUrl = 'https://leverade.medium.com/' +
  'how-to-claim-your-digital-trophies-medals-or-certificates-with-an-email-wallet-step-by-step-3e6bf0e49c44'

const howToCreateGalleryUrl = 'https://leverade.medium.com/' +
  'how-to-create-your-athlete-gallery-and-showcase-your-digital-achievements-with-the-world-step-by-074c2930c26c'

export const Faq: FC<Props> = ({ appearance = 'default', showTitle = true }) => {
  const { t } = useTranslation('faq')
  const [openQuestionIndex, setOpenQuestionIndex] = useState<number | null>(null)
  const items = [
    {
      index: 1,
      question: t('question_1'),
      answer: (
        <>
          <p className={ styles.faq__questionAnswer__para }>{ t('answer_1_1') }</p>
          <p className={ styles.faq__questionAnswer__para }>{ t('answer_1_2') }</p>
          <p className={ styles.faq__questionAnswer__para }>{ t('answer_1_3') }</p>
        </>
      ),
    },
    {
      index: 2,
      question: t('question_2'),
      answer: t('answer_2'),
    },
    {
      index: 3,
      question: t('question_3'),
      answer: (
        <Trans
          i18nKey={ t('answer_3') }
          components={ [
            <a
              className={ styles.faq__link }
              key="link-how-to-mint"
              href={ howToMintUrl }
              target="_blank"
              rel="noreferrer"
            />,
          ] }
        />
      ),
    },
    {
      index: 4,
      question: t('question_4'),
      answer: t('answer_4'),
    },
    {
      index: 5,
      question: t('question_5'),
      answer: (
        <Trans
          i18nKey={ t('answer_5') }
          components={ [
            <a
              className={ styles.faq__link }
              key="link-how-to-gallery"
              href={ howToCreateGalleryUrl }
              target="_blank"
              rel="noreferrer"
            />,
          ] }
        />
      ),
    },
    {
      index: 6,
      question: t('question_6'),
      answer: t('answer_6'),
    },
  ]

  function changeOpenIndex (index: typeof openQuestionIndex): void {
    if (openQuestionIndex === index) {
      setOpenQuestionIndex(null)

      return
    }

    setOpenQuestionIndex(index)
  }

  return (
    <section
      className={ `
        ${styles.faq__container}
        ${appearance === 'museumHome' ? styles.museumHome : ''}
      ` }
    >
      { showTitle && (
        <div className={ styles.faq__title }>
          { t('section_title') }
        </div>
      ) }

      { items.map((item) => {
        const isOpen = openQuestionIndex === item.index
        const answerId = `faq-answer-${item.index}`
        const buttonId = `faq-question-${item.index}`

        return (
          <div
            key={ item.index }
            className={ `
              ${styles.faq__questionContainer}
              ${isOpen ? styles.faq__questionContainer_open : ''}
            ` }
            data-qa={ 'question-container' }
          >
            <button
              type="button"
              id={ buttonId }
              className={ styles.faq__questionTitle }
              onClick={ () => changeOpenIndex(item.index) }
              data-qa={ 'question-title' }
              aria-expanded={ isOpen }
              aria-controls={ answerId }
            >
              <span>{ item.question }</span>
              <FontAwesomeIcon
                className={ styles.faq__questionIcon }
                icon={ faAngleDown }
                rotation={ isOpen ? 180 : undefined }
                aria-hidden={ true }
              />
            </button>
            <div
              id={ answerId }
              className={ `
                ${styles.faq__questionAnswer}
                ${isOpen ? styles.faq__questionAnswer_open : ''}
              ` }
              data-qa={ 'question-answer' }
              role="region"
              aria-labelledby={ buttonId }
            >
              { item.answer }
            </div>
          </div>
        )
      }) }
    </section>
  )
}
